import React, { useState, useMemo } from 'react';
import { useAccount } from 'wagmi';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import {
    Activity,
    Cpu,
    ExternalLink,
    TrendingUp,
    TrendingDown,
    Zap,
    BarChart2,
    Target,
    ChevronRight,
    Bot,
    Minus,
    Globe,
    Wifi,
    WifiOff,
} from 'lucide-react';
import {
    DUCKMON_AGENTS,
} from '../../types/agent';
import { getTokenImageUrl } from '../../lib/tokenImages';
import { useDashboardData } from '../../hooks/useDuckSignals';
import { useTokenPrice } from '../../hooks/useTokenPrice';
import { useAgentSocket } from '../../hooks/useAgentSocket';
import { TokenConfig, POPULAR_MONAD_TOKENS } from '../../lib/WebAgentEngine';
import AgentArchitecture from './AgentArchitecture';
import TokenSelector from './TokenSelector';
import LiveAnalysis from './LiveAnalysis';
import AutoTrader from '../sections/AutoTrader';
import WhaleActivity from './WhaleActivity';
import AdvancedAgents from './AdvancedAgents';
import AdvancedAgentsFeed from './AdvancedAgentsFeed';
import AgentPerformance from './AgentPerformance';
import SentimentPanel from './SentimentPanel';
import LiquidityMonitor from './LiquidityMonitor';
import OnChainAnalytics from './OnChainAnalytics';
import EnhancedTokenFeed from './EnhancedTokenFeed';

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN SYSTEM
// ═══════════════════════════════════════════════════════════════════════════════

const THEME = {
    purple: '#836EF9',
    purpleLight: '#A08BFF',
    purpleDark: '#5e17eb',
    yellow: '#FFD700',
    orange: '#FF8C00',
    green: '#22c55e',
    red: '#ef4444',
    bg: 'linear-gradient(135deg, #5e17eb 0%, #3e0eb3 50%, #2a0a80 100%)',
    card: 'linear-gradient(145deg, rgba(131, 110, 249, 0.15) 0%, rgba(94, 23, 235, 0.1) 100%)',
    border: 'rgba(181, 111, 111, 0.2)',
    glow: '0 0 40px rgba(255, 215, 0, 0.15)',
};

const styles = {
    container: {
        minHeight: '100vh',
        background: THEME.bg,
        padding: '8rem 2rem 2rem 2rem',
    } as React.CSSProperties,
    header: {
        maxWidth: '1400px',
        margin: '0 auto 2rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    } as React.CSSProperties,
    title: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
    } as React.CSSProperties,
    logoBox: {
        width: '48px',
        height: '48px',
        borderRadius: '12px',
        background: `linear-gradient(135deg, ${THEME.purple}, ${THEME.yellow})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: `0 0 20px ${THEME.purple}40`,
        padding: '6px',
        overflow: 'hidden',
    } as React.CSSProperties,
    titleText: {
        fontSize: '1.75rem',
        fontWeight: 800,
        color: '#fff',
        margin: 0,
        letterSpacing: '-0.02em',
    } as React.CSSProperties,
    subtitle: {
        fontSize: '0.875rem',
        color: '#9ca3af',
        margin: '0.25rem 0 0',
    } as React.CSSProperties,
    tabContainer: {
        maxWidth: '1400px',
        margin: '0 auto 1.5rem',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.375rem',
        padding: '0.5rem',
        background: 'rgba(0,0,0,0.3)',
        borderRadius: '16px',
        border: `1px solid ${THEME.border}`,
    } as React.CSSProperties,
    tab: (active: boolean) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '0.375rem',
        padding: '0.5rem 0.875rem',
        borderRadius: '10px',
        border: 'none',
        cursor: 'pointer',
        fontSize: '0.8rem',
        fontWeight: 600,
        transition: 'all 0.2s',
        whiteSpace: 'nowrap',
        background: active ? `linear-gradient(135deg, ${THEME.purple}30, ${THEME.purple}10)` : 'transparent',
        color: active ? '#fff' : '#9ca3af',
        boxShadow: active ? `0 0 20px ${THEME.purple}20` : 'none',
    } as React.CSSProperties),
    content: {
        maxWidth: '1400px',
        margin: '0 auto',
    } as React.CSSProperties,
    card: {
        background: THEME.card,
        border: `1px solid ${THEME.border}`,
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: THEME.glow,
    } as React.CSSProperties,
    cardHeader: {
        padding: '1.25rem 1.5rem',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
    } as React.CSSProperties,
    cardTitle: {
        fontSize: '0.875rem',
        fontWeight: 700,
        color: '#fff',
        margin: 0,
        letterSpacing: '0.02em',
        textTransform: 'uppercase' as const,
    },
    cardBody: {
        padding: '1.5rem',
    } as React.CSSProperties,
    statGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '1rem',
        marginBottom: '1.5rem',
    } as React.CSSProperties,
    statCard: (color: string) => ({
        background: `${color}10`,
        border: `1px solid ${color}25`,
        borderRadius: '16px',
        padding: '1.25rem',
        textAlign: 'center' as const,
    }),
    statValue: (color: string) => ({
        fontSize: '2rem',
        fontWeight: 800,
        color,
        margin: 0,
    }),
    statLabel: {
        fontSize: '0.75rem',
        color: '#9ca3af',
        marginTop: '0.25rem',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
    } as React.CSSProperties,
    liveBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.5rem 1rem',
        background: `${THEME.green}20`,
        border: `1px solid ${THEME.green}40`,
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: 700,
        color: THEME.green,
    } as React.CSSProperties,
    pulse: {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        background: THEME.green,
        animation: 'pulse 2s infinite',
    } as React.CSSProperties,
};

type DashboardTab = 'overview' | 'analysis' | 'signals' | 'architecture' | 'tournaments' | 'autotrader' | 'whales' | 'agents' | 'advanced-feed' | 'performance' | 'sentiment' | 'liquidity' | 'onchain';

// Time ago helper
function timeAgo(timestamp: number): string {
    const diff = (Date.now() - timestamp) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}

// Signal icon
const SignalIcon = ({ type }: { type: string }) => {
    if (type === 'BUY') return <TrendingUp size={16} color={THEME.green} />;
    if (type === 'SELL') return <TrendingDown size={16} color={THEME.red} />;
    return <Minus size={16} color={THEME.yellow} />;
};

// ═══════════════════════════════════════════════════════════════════════════════
// OVERVIEW TAB
// ═══════════════════════════════════════════════════════════════════════════════

const OverviewTab: React.FC = () => {
    const { isDeployed, agentCount, totalSignals, totalPredictions, recentSignals, recentPredictions, isLoading } = useDashboardData();

    // Signal distribution for pie chart
    const signalDistribution = useMemo(() => {
        if (!recentSignals || recentSignals.length === 0) return [];
        let buy = 0, sell = 0, hold = 0;
        for (const s of recentSignals) {
            if (s.signalType === 'BUY') buy++;
            else if (s.signalType === 'SELL') sell++;
            else hold++;
        }
        return [
            { name: 'BUY', value: buy, color: THEME.green },
            { name: 'SELL', value: sell, color: THEME.red },
            { name: 'HOLD', value: hold, color: THEME.yellow },
        ].filter(d => d.value > 0);
    }, [recentSignals]);

    // Confidence trend for line chart
    const confidenceTrend = useMemo(() => {
        if (!recentSignals || recentSignals.length === 0) return [];
        return recentSignals
            .slice()
            .reverse()
            .map((s, i) => ({
                index: i + 1,
                confidence: Number(s.confidence),
            }));
    }, [recentSignals]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Stats Grid */}
            <div style={styles.statGrid}>
                <div style={styles.statCard(THEME.purple)}>
                    <div style={styles.statValue(THEME.purple)}>{DUCKMON_AGENTS.length}</div>
                    <div style={styles.statLabel}>AI Agents</div>
                </div>
                <div style={styles.statCard(THEME.yellow)}>
                    <div style={styles.statValue(THEME.yellow)}>{totalSignals}</div>
                    <div style={styles.statLabel}>On-Chain Signals</div>
                </div>
                <div style={styles.statCard(THEME.green)}>
                    <div style={styles.statValue(THEME.green)}>{totalPredictions}</div>
                    <div style={styles.statLabel}>Predictions</div>
                </div>
                <div style={styles.statCard(THEME.orange)}>
                    <div style={styles.statValue(THEME.orange)}>{agentCount}</div>
                    <div style={styles.statLabel}>Registered</div>
                </div>
            </div>

            {/* Charts Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                {/* Signal Distribution Pie */}
                <div style={styles.card}>
                    <div style={styles.cardHeader}>
                        <TrendingUp size={18} color={THEME.green} />
                        <h3 style={styles.cardTitle}>Signal Distribution</h3>
                    </div>
                    <div style={styles.cardBody}>
                        {signalDistribution.length > 0 ? (
                            <div style={{ textAlign: 'center' }}>
                                <ResponsiveContainer width="100%" height={180}>
                                    <PieChart>
                                        <Pie
                                            data={signalDistribution}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={50}
                                            outerRadius={75}
                                            dataKey="value"
                                            stroke="none"
                                        >
                                            {signalDistribution.map((entry, i) => (
                                                <Cell key={i} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(131,110,249,0.3)', borderRadius: '8px' }}
                                            labelStyle={{ color: '#fff' }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', marginTop: '0.5rem' }}>
                                    {signalDistribution.map((d, i) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                                            <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: d.color }} />
                                            <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{d.name}: {d.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem 0' }}>No signal data yet</p>
                        )}
                    </div>
                </div>

                {/* Confidence Trend Line */}
                <div style={styles.card}>
                    <div style={styles.cardHeader}>
                        <Activity size={18} color={THEME.purple} />
                        <h3 style={styles.cardTitle}>Confidence Trend</h3>
                    </div>
                    <div style={styles.cardBody}>
                        {confidenceTrend.length > 0 ? (
                            <ResponsiveContainer width="100%" height={200}>
                                <LineChart data={confidenceTrend}>
                                    <XAxis dataKey="index" stroke="#6b7280" fontSize={11} />
                                    <YAxis domain={[0, 100]} stroke="#6b7280" fontSize={11} />
                                    <Tooltip
                                        contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(131,110,249,0.3)', borderRadius: '8px' }}
                                        labelStyle={{ color: '#fff' }}
                                    />
                                    <Line type="monotone" dataKey="confidence" stroke={THEME.purple} strokeWidth={2} dot={{ r: 3 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem 0' }}>No confidence data yet</p>
                        )}
                    </div>
                </div>
            </div>

            {/* System Status */}
            <div style={styles.card}>
                <div style={styles.cardHeader}>
                    <Zap size={18} color={THEME.yellow} />
                    <h3 style={styles.cardTitle}>System Status</h3>
                </div>
                <div style={styles.cardBody}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', background: 'rgba(0,0,0,0.3)', borderRadius: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: isDeployed ? THEME.green : THEME.red }} />
                                <span style={{ color: '#fff', fontWeight: 500 }}>DuckSignals Contract</span>
                            </div>
                            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: isDeployed ? THEME.green : THEME.red }}>
                                {isDeployed ? 'DEPLOYED' : 'NOT DEPLOYED'}
                            </span>
                        </div>
                        {DUCKMON_AGENTS.map(agent => (
                            <div key={agent.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', background: 'rgba(0,0,0,0.3)', borderRadius: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: agent.status === 'running' ? THEME.green : agent.status === 'beta' ? THEME.yellow : THEME.red }} />
                                    <span style={{ color: '#fff', fontWeight: 500 }}>{agent.name}</span>
                                </div>
                                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: agent.status === 'running' ? THEME.green : agent.status === 'beta' ? THEME.yellow : THEME.red }}>
                                    {agent.status === 'running' ? (agent.onChainEnabled ? 'LIVE' : 'RUNNING') : agent.status === 'beta' ? 'BETA' : 'STOPPED'}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Recent Signals */}
            <div style={styles.card}>
                <div style={{ ...styles.cardHeader, justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <Activity size={18} color={THEME.purple} />
                        <h3 style={styles.cardTitle}>Recent Signals</h3>
                    </div>
                    <div style={styles.liveBadge}>
                        <span style={styles.pulse} />
                        LIVE
                    </div>
                </div>
                <div style={styles.cardBody}>
                    {isLoading ? (
                        <p style={{ color: '#9ca3af', textAlign: 'center' }}>Loading signals...</p>
                    ) : recentSignals && recentSignals.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {recentSignals.map((signal, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem', background: 'rgba(0,0,0,0.3)', borderRadius: '12px' }}>
                                    <SignalIcon type={signal.signalType} />
                                    <span style={{ fontWeight: 700, color: signal.signalType === 'BUY' ? THEME.green : signal.signalType === 'SELL' ? THEME.red : THEME.yellow }}>
                                        {signal.signalType}
                                    </span>
                                    <span style={{ color: '#fff', fontWeight: 600 }}>{Number(signal.confidence)}%</span>
                                    <span style={{ flex: 1, color: '#6b7280', fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {signal.reason || 'Agent analysis'}
                                    </span>
                                    <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>{timeAgo(Number(signal.timestamp) * 1000)}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p style={{ color: '#6b7280', textAlign: 'center' }}>No signals yet. Agents post every 15 minutes.</p>
                    )}
                </div>
            </div>

            {/* Recent Predictions */}
            <div style={styles.card}>
                <div style={{ ...styles.cardHeader, justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <Target size={18} color={THEME.green} />
                        <h3 style={styles.cardTitle}>Recent Predictions</h3>
                    </div>
                    <div style={styles.liveBadge}>
                        <span style={styles.pulse} />
                        LIVE
                    </div>
                </div>
                <div style={styles.cardBody}>
                    {isLoading ? (
                        <p style={{ color: '#9ca3af', textAlign: 'center' }}>Loading predictions...</p>
                    ) : recentPredictions && recentPredictions.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {recentPredictions.map((pred, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem', background: 'rgba(0,0,0,0.3)', borderRadius: '12px' }}>
                                    <SignalIcon type={pred.direction === 'UP' ? 'BUY' : pred.direction === 'DOWN' ? 'SELL' : 'HOLD'} />
                                    <span style={{ fontWeight: 700, color: pred.direction === 'UP' ? THEME.green : THEME.red }}>
                                        {pred.direction}
                                    </span>
                                    <span style={{ color: '#fff', fontWeight: 600 }}>{Number(pred.confidence)}% confidence</span>
                                    <span style={{ marginLeft: 'auto', color: '#6b7280', fontSize: '0.75rem' }}>{timeAgo(Number(pred.timestamp) * 1000)}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p style={{ color: '#6b7280', textAlign: 'center' }}>No predictions yet. Bot runs every 15 minutes.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ANALYSIS TAB
// ═══════════════════════════════════════════════════════════════════════════════

const AnalysisTab: React.FC<{ selectedToken: TokenConfig | null }> = ({
    selectedToken,
}) => {
    const price = useTokenPrice(selectedToken);

    const getDexScreenerEmbed = (address: string) => `https://dexscreener.com/monad/${address}?embed=1&theme=dark&trades=0&info=0`;
    const getGmgnUrl = (address: string) => `https://gmgn.ai/monad/token/nadfun_${address}`;

    const formatMonPrice = (p: number) => {
        if (p === 0) return 'Loading...';
        if (p >= 1) return `${p.toFixed(4)} MON`;
        const str = p.toFixed(18);
        const decimals = str.split('.')[1] || '';
        let leadingZeros = 0;
        for (const c of decimals) {
            if (c === '0') leadingZeros++;
            else break;
        }
        if (leadingZeros >= 4) {
            const significant = decimals.slice(leadingZeros, leadingZeros + 4);
            return `0.0\u2080${String.fromCharCode(0x2080 + leadingZeros)}${significant} MON`;
        }
        return `${p.toPrecision(4)} MON`;
    };

    const formatUsdPrice = (p: number) => {
        if (p === 0) return '';
        if (p >= 1) return `$${p.toFixed(2)}`;
        if (p >= 0.01) return `$${p.toFixed(4)}`;
        return `$${p.toPrecision(4)}`;
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Cpu size={24} color={THEME.purple} />
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff', margin: 0 }}>Live Token Analysis</h3>
            </div>

            {selectedToken ? (
                <>
                    {/* Price Summary Bar */}
                    <div style={{
                        ...styles.card,
                        padding: '1.25rem 1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '1rem',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <div style={{
                                width: '40px', height: '40px', borderRadius: '12px',
                                background: `linear-gradient(135deg, ${THEME.purple}, ${THEME.yellow})`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                overflow: 'hidden',
                            }}>
                                <img
                                    src={getTokenImageUrl(selectedToken.symbol, selectedToken.address)}
                                    alt={selectedToken.symbol}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                        (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                />
                            </div>
                            <div>
                                <div style={{ fontSize: '1rem', fontWeight: 700, color: '#fff' }}>
                                    {selectedToken.symbol}
                                    <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.5rem', fontWeight: 400 }}>
                                        {selectedToken.name}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginTop: '0.25rem' }}>
                                    <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#fff' }}>
                                        {price.isLoading ? '...' : formatMonPrice(price.priceInMon)}
                                    </span>
                                    {price.priceUsd > 0 && (
                                        <span style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                                            ≈ {formatUsdPrice(price.priceUsd)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            {!price.isNativeAsset && (
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.65rem', color: '#6b7280', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        {price.isGraduated ? 'GRADUATED' : 'BONDING'}
                                    </div>
                                    <div style={{
                                        width: '80px', height: '6px', background: 'rgba(0,0,0,0.4)',
                                        borderRadius: '9999px', overflow: 'hidden',
                                    }}>
                                        <div style={{
                                            width: `${price.isGraduated ? 100 : price.bondingProgress}%`,
                                            height: '100%', borderRadius: '9999px',
                                            background: price.isGraduated
                                                ? `linear-gradient(90deg, ${THEME.green}, ${THEME.yellow})`
                                                : `linear-gradient(90deg, ${THEME.purple}, ${THEME.yellow})`,
                                            transition: 'width 1s ease',
                                        }} />
                                    </div>
                                    <div style={{ fontSize: '0.7rem', fontWeight: 600, color: price.isGraduated ? THEME.green : THEME.yellow, marginTop: '0.15rem' }}>
                                        {price.isGraduated ? '✓ DEX' : `${price.bondingProgress.toFixed(1)}%`}
                                    </div>
                                </div>
                            )}
                            <div style={{
                                padding: '0.5rem 0.75rem',
                                background: 'rgba(0,0,0,0.3)',
                                borderRadius: '8px',
                                fontSize: '0.75rem',
                                color: '#9ca3af',
                            }}>
                                1 MON ≈ <span style={{ color: '#fff', fontWeight: 600 }}>
                                    {price.monUsdRate > 0 ? `$${price.monUsdRate.toFixed(2)}` : '...'}
                                </span>
                            </div>
                            <div style={styles.liveBadge}>
                                <span style={styles.pulse} />
                                RPC LIVE
                            </div>
                        </div>
                    </div>

                    <div style={styles.card}>
                        <div style={{ ...styles.cardHeader, justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <BarChart2 size={18} color={THEME.yellow} />
                                <h3 style={styles.cardTitle}>Price Chart</h3>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <a href={getGmgnUrl(selectedToken.address)} target="_blank" rel="noopener noreferrer"
                                    style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.5rem 0.75rem', background: `${THEME.green}20`, borderRadius: '8px', color: THEME.green, fontSize: '0.75rem', fontWeight: 600, textDecoration: 'none' }}>
                                    GMGN <ExternalLink size={12} />
                                </a>
                            </div>
                        </div>
                        <div style={{ height: '400px', background: 'rgba(0,0,0,0.3)' }}>
                            <iframe
                                src={getDexScreenerEmbed(selectedToken.address)}
                                style={{ width: '100%', height: '100%', border: 'none' }}
                                title="Price Chart"
                            />
                        </div>
                    </div>

                    <LiveAnalysis
                        token={selectedToken}
                        currentPrice={price.priceUsd > 0 ? price.priceUsd : price.priceInMon}
                        priceHistory={price.priceHistory}
                        priceInMon={price.priceInMon}
                        monUsdRate={price.monUsdRate}
                    />
                </>
            ) : (
                <div style={{ ...styles.card, padding: '3rem', textAlign: 'center' }}>
                    <Target size={48} color={THEME.purple} style={{ marginBottom: '1rem' }} />
                    <p style={{ color: '#9ca3af' }}>Select a token to start analysis</p>
                </div>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SIGNALS TAB
// ═══════════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════════
// TOURNAMENTS TAB
// ═══════════════════════════════════════════════════════════════════════════════

const TournamentsTab: React.FC = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Target size={24} color={THEME.yellow} />
            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff', margin: 0 }}>Agent Tournaments</h3>
            <span style={{ padding: '0.25rem 0.75rem', background: `${THEME.orange}20`, color: THEME.orange, borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600 }}>
                COMING SOON
            </span>
        </div>

        <div style={{
            ...styles.card,
            padding: '3rem',
            textAlign: 'center',
        }}>
            <Target size={48} color={THEME.purple} style={{ marginBottom: '1rem' }} />
            <h4 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff', margin: '0 0 0.75rem' }}>
                DAP Arena
            </h4>
            <p style={{ color: '#9ca3af', fontSize: '0.875rem', maxWidth: '400px', margin: '0 auto 1.5rem', lineHeight: 1.6 }}>
                Agent tournaments with on-chain scoring and prize pools.
                AI agents compete in real-time trading battles.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                {['Trading Battles', 'Prediction Leagues', 'Strategy Arenas'].map(feature => (
                    <span key={feature} style={{
                        padding: '0.5rem 1rem',
                        background: `${THEME.purple}15`,
                        border: `1px solid ${THEME.purple}30`,
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        color: THEME.purpleLight,
                        fontWeight: 600,
                    }}>
                        {feature}
                    </span>
                ))}
            </div>
        </div>
    </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN DASHBOARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const AgentDashboard: React.FC = () => {
    const { isConnected } = useAccount();
    const [activeTab, setActiveTab] = useState<DashboardTab>('analysis');
    const [selectedToken, setSelectedToken] = useState<TokenConfig | null>(POPULAR_MONAD_TOKENS[0] || null);

    // Real-time agent connection
    const agentSocket = useAgentSocket(selectedToken?.address);
    const heartbeatAgents = Object.values(agentSocket.agents).filter(a => a.isAlive).length;
    const runningAgents = DUCKMON_AGENTS.filter(a => a.status === 'running').length;
    const aliveAgents = heartbeatAgents > 0 ? heartbeatAgents : runningAgents;

    const tabs: { id: DashboardTab; label: string; icon: typeof Bot }[] = [
        { id: 'advanced-feed', label: 'Live Feed', icon: Activity },
        { id: 'agents', label: 'All Agents', icon: Bot },
        { id: 'analysis', label: 'Live Analysis', icon: Cpu },
        { id: 'performance', label: 'Performance', icon: BarChart2 },
        { id: 'sentiment', label: 'Sentiment', icon: TrendingUp },
        { id: 'liquidity', label: 'Liquidity', icon: Target },
        { id: 'onchain', label: 'On-Chain', icon: Globe },
        { id: 'autotrader', label: 'Auto-Pilot', icon: Bot },
        { id: 'whales', label: 'Whale Watch', icon: Globe },
        { id: 'overview', label: 'Overview', icon: BarChart2 },
        { id: 'signals', label: 'Signals', icon: Activity },
        { id: 'architecture', label: 'How It Works', icon: Zap },
        { id: 'tournaments', label: 'Tournaments', icon: Target },
    ];

    return (
        <section id="agents" style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <div style={styles.title}>
                    <div style={styles.logoBox}>
                        <img
                            src="/images/duck.png"
                            alt="Duck Logo"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                            }}
                            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                // Fallback to Bot icon if image fails to load
                                e.currentTarget.style.display = 'none';
                            }}
                        />
                    </div>
                    <div>
                        <h2 style={styles.titleText}>Agent Protocol</h2>
                        <p style={styles.subtitle}>AI-powered trading intelligence on Monad blockchain</p>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {/* Agent WebSocket Status */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.375rem 0.75rem',
                        background: agentSocket.isConnected ? 'rgba(34, 197, 94, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                        border: `1px solid ${agentSocket.isConnected ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: agentSocket.isConnected ? '#22c55e' : '#ef4444',
                    }}>
                        {agentSocket.isConnected ? <Wifi size={14} /> : <WifiOff size={14} />}
                        {agentSocket.isConnected ? `${aliveAgents} Agents Live` : 'Agents Offline'}
                    </div>

                    {isConnected ? (
                        <div style={styles.liveBadge}>
                            <span style={styles.pulse} />
                            Connected
                        </div>
                    ) : (
                        <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Connect wallet for full access</span>
                    )}
                </div>
            </div>

            {/* Global Token Selector */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 0 0.5rem', marginTop: '-0.5rem' }}>
                <TokenSelector selectedToken={selectedToken} onSelectToken={setSelectedToken} />
            </div>

            {/* Tabs */}
            <div style={styles.tabContainer}>
                {tabs.map(({ id, label, icon: Icon }) => (
                    <button
                        key={id}
                        onClick={() => setActiveTab(id)}
                        style={styles.tab(activeTab === id)}
                    >
                        <Icon size={18} />
                        <span>{label}</span>
                        {activeTab === id && <ChevronRight size={14} />}
                    </button>
                ))}
            </div>

            {/* Content */}
            {activeTab === 'advanced-feed' && <AdvancedAgentsFeed tokenAddress={selectedToken?.address} agentSocket={agentSocket} />}
            <div style={styles.content}>
                {activeTab === 'agents' && <AdvancedAgents />}
                {activeTab === 'analysis' && <AnalysisTab selectedToken={selectedToken} />}
                {activeTab === 'performance' && <AgentPerformance />}
                {activeTab === 'sentiment' && <SentimentPanel tokenAddress={selectedToken?.address} />}
                {activeTab === 'liquidity' && <LiquidityMonitor tokenAddress={selectedToken?.address} />}
                {activeTab === 'onchain' && <OnChainAnalytics tokenAddress={selectedToken?.address} />}
                {activeTab === 'autotrader' && <AutoTrader />}
                {activeTab === 'overview' && <OverviewTab />}
                {activeTab === 'signals' && <EnhancedTokenFeed tokenAddress={selectedToken?.address} />}
                {activeTab === 'architecture' && <AgentArchitecture />}
                {activeTab === 'whales' && <WhaleActivity selectedToken={selectedToken} whaleAlerts={agentSocket.whaleAlerts} />}
                {activeTab === 'tournaments' && <TournamentsTab />}
            </div>
        </section>
    );
};

export default AgentDashboard;
